import { useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import {
  DEFAULT_MINI_VISIBILITY,
  MINI_NOTCHED_SETTING_KEY,
  MINI_VISIBILITY_SETTING_KEY,
  type MiniVisibility,
} from '@shared/mini-settings'
import {
  applyMiniPosition,
  MINI_POSITION_SETTING_KEY,
  resolveMiniPosition,
  type MiniPosition,
  type MiniPositionStore,
} from '@shared/mini/display-position'
import { useOverlayDisplayKey } from '@renderer/overlay/hooks/use-overlay-display-key'
import { overlayDisplayKey } from '@shared/overlay/display'

/**
 * Custom drag control interface for Mini's pointer-event-based drag system.
 * Compatible with the drag handle's `start(e)` call pattern.
 */
export interface MiniDragControl {
  start(event: ReactPointerEvent<Element> | PointerEvent): void
}

/**
 * Module-level ref for drag controls (singleton pattern for Mini)
 */
export const miniDragControlsRef: { current: MiniDragControl | null } = {
  current: null,
}

/**
 * Mini dragging state
 */
const miniDraggingAtom = atom(false)
export const useIsMiniDragging = () => useAtomValue(miniDraggingAtom)
export const useSetMiniDragging = () => useSetAtom(miniDraggingAtom)
export const useMiniDragging = () => useAtom(miniDraggingAtom)

/**
 * Mini hovered state
 */
const miniHoveredAtom = atom(false)
export const useIsMiniHovered = () => useAtomValue(miniHoveredAtom)
export const useSetMiniHovered = () => useSetAtom(miniHoveredAtom)
export const useMiniHovered = () => useAtom(miniHoveredAtom)

/**
 * Mini drag handle hovered state
 */
const miniDragHandleHoveredAtom = atom(false)
export const useIsMiniDragHandleHovered = () => useAtomValue(miniDragHandleHoveredAtom)
export const useSetMiniDragHandleHovered = () => useSetAtom(miniDragHandleHoveredAtom)
export const useMiniDragHandleHovered = () => useAtom(miniDragHandleHoveredAtom)

/**
 * Mini on left side state - persisted to survive sidepanel open/close
 */
const miniOnLeftSideAtom = atomWithGlobalStore<boolean>('settings', 'mini.isOnLeftSide', true)
export const useIsMiniOnLeftSide = () => useAtomValue(miniOnLeftSideAtom)
export const useSetMiniOnLeftSide = () => useSetAtom(miniOnLeftSideAtom)
export const useMiniOnLeftSide = () => useAtom(miniOnLeftSideAtom)

/**
 * Mini docked into the display's notch region (top center) - persisted like the side
 */
const miniNotchedAtom = atomWithGlobalStore<boolean>('settings', MINI_NOTCHED_SETTING_KEY, false)
export const useIsMiniNotched = () => useAtomValue(miniNotchedAtom)
export const useSetMiniNotched = () => useSetAtom(miniNotchedAtom)
export const useMiniNotched = () => useAtom(miniNotchedAtom)

/**
 * Mini recording state
 */
const miniRecordingAtom = atom(false)
export const useIsMiniRecording = () => useAtomValue(miniRecordingAtom)
export const useSetMiniRecording = () => useSetAtom(miniRecordingAtom)
export const useMiniRecording = () => useAtom(miniRecordingAtom)

/**
 * Mini paused state
 */
const miniPausedAtom = atom(false)
export const useIsMiniPaused = () => useAtomValue(miniPausedAtom)
export const useSetMiniPaused = () => useSetAtom(miniPausedAtom)

/**
 * Mini content hovered state
 */
const miniContentHoveredAtom = atom(false)
export const useIsMiniContentHovered = () => useAtomValue(miniContentHoveredAtom)
export const useSetMiniContentHovered = () => useSetAtom(miniContentHoveredAtom)
export const useMiniContentHovered = () => useAtom(miniContentHoveredAtom)

/**
 * Mini menu open state - prevents onMouseLeave from unhover-ing while menu is open
 */
interface MiniOpenSurfaceUpdate {
  id: string
  open: boolean
}

const miniOpenSurfaceIdsAtom = atom<ReadonlySet<string>>(new Set<string>())
const miniMenuOpenAtom = atom((get) => get(miniOpenSurfaceIdsAtom).size > 0)
const setMiniOpenSurfaceAtom = atom(null, (get, set, { id, open }: MiniOpenSurfaceUpdate) => {
  const current = get(miniOpenSurfaceIdsAtom)
  if (current.has(id) === open) {
    return
  }

  const next = new Set(current)
  if (open) {
    next.add(id)
  } else {
    next.delete(id)
  }
  set(miniOpenSurfaceIdsAtom, next)
})

export const useIsMiniMenuOpen = () => useAtomValue(miniMenuOpenAtom)
export const useSetMiniOpenSurface = () => useSetAtom(setMiniOpenSurfaceAtom)

const miniRevealedAtom = atom((get) => get(miniHoveredAtom) || get(miniDraggingAtom) || get(miniMenuOpenAtom))
export const useIsMiniRevealed = () => useAtomValue(miniRevealedAtom)

/**
 * Where the user parked Mini, remembered per monitor — the overlay is one
 * window that migrates between displays, so a single stored point can't
 * survive the round trip (see `@shared/mini/display-position`).
 */
const miniPositionAtom = atomWithGlobalStore<MiniPositionStore>('settings', MINI_POSITION_SETTING_KEY, {})

/** `null` until the user has placed Mini somewhere; callers supply their own default. */
export const useMiniPosition = (): MiniPosition | null => {
  const store = useAtomValue(miniPositionAtom)
  const displayKey = useOverlayDisplayKey()
  return useMemo(() => resolveMiniPosition(store, displayKey), [store, displayKey])
}

/**
 * `displayId` overrides the monitor to store against, for the same reason as
 * {@link useSetSidepanelGeometry}: a drag that ends on another screen commits
 * before main's `overlay.displayId` broadcast has come back.
 */
export const useSetMiniPosition = () => {
  const setPosition = useSetAtom(miniPositionAtom)
  // Through a ref, not a dependency, so the callback identity stays stable
  // across display moves and `startDrag` is not re-created mid-gesture.
  const displayKey = useOverlayDisplayKey()
  const displayKeyRef = useRef(displayKey)
  displayKeyRef.current = displayKey

  return useCallback(
    (position: MiniPosition, displayId?: number | null) => {
      const key = overlayDisplayKey(displayId) ?? displayKeyRef.current
      setPosition((store) => applyMiniPosition(store, key, position))
    },
    [setPosition],
  )
}

const miniVisibilityAtom = atomWithGlobalStore<MiniVisibility>(
  'settings',
  MINI_VISIBILITY_SETTING_KEY,
  DEFAULT_MINI_VISIBILITY,
)
export const useMiniVisibility = () => useAtomValue(miniVisibilityAtom)
export const useSetMiniVisibility = () => useSetAtom(miniVisibilityAtom)
